if (!customElements.get('quick-view-modal')) {
  customElements.define(
    'quick-view-modal',
    class QuickViewModal extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0;
        this.images = [];
        this.productData = null;
        this.init();
      }

      init() {
        const closeButton = this.querySelector('#QuickViewModalClose');
        if (closeButton) {
          closeButton.addEventListener('click', () => this.hide());
        }

        // Close on backdrop click
        this.addEventListener('click', (event) => {
          if (event.target === this) {
            this.hide();
          }
        });

        // Close on ESC key
        this.addEventListener('keyup', (event) => {
          if (event.code === 'Escape') {
            this.hide();
          }
        });

        // Carousel navigation
        const prevButton = this.querySelector('.quick-view-carousel__prev');
        const nextButton = this.querySelector('.quick-view-carousel__next');
        
        if (prevButton) {
          prevButton.addEventListener('click', () => this.prevImage());
        }
        if (nextButton) {
          nextButton.addEventListener('click', () => this.nextImage());
        }

        // Touch/swipe support for mobile
        this.initSwipe();
      }

      initSwipe() {
        const track = this.querySelector('.quick-view-carousel__track');
        if (!track) return;

        let startX = 0;
        let currentX = 0;
        let isDragging = false;

        track.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
          isDragging = true;
        });

        track.addEventListener('touchmove', (e) => {
          if (!isDragging) return;
          currentX = e.touches[0].clientX;
        });

        track.addEventListener('touchend', () => {
          if (!isDragging) return;
          isDragging = false;

          const diff = startX - currentX;
          const threshold = 50;

          if (Math.abs(diff) > threshold) {
            if (diff > 0) {
              this.nextImage();
            } else {
              this.prevImage();
            }
          }
        });
      }

      async show(productHandle) {
        this.setAttribute('open', 'true');
        document.body.classList.add('overflow-hidden');
        
        // Reset scroll position to top when modal opens
        const contentEl = this.querySelector('.quick-view-modal__content');
        if (contentEl) {
          contentEl.scrollTop = 0;
        }
        
        const loadingEl = this.querySelector('.quick-view-modal__loading');
        const bodyEl = this.querySelector('.quick-view-modal__body');
        
        // Show loading, hide content
        loadingEl.style.display = 'flex';
        loadingEl.style.visibility = 'visible';
        loadingEl.style.opacity = '1';
        bodyEl.style.display = 'none';

        try {
          // Fetch product data from Shopify JSON API
          const response = await fetch(`/products/${productHandle}.js`);
          if (!response.ok) throw new Error('Failed to fetch product');
          
          this.productData = await response.json();
          this.renderProduct();
          
          // Hide loading, show content
          loadingEl.style.display = 'none';
          loadingEl.style.visibility = 'hidden';
          loadingEl.style.opacity = '0';
          bodyEl.style.display = 'block';
          
          // Ensure scroll is at top after content loads
          if (contentEl) {
            contentEl.scrollTop = 0;
          }
          
          // Focus trap
          this.trapFocus();
        } catch (error) {
          console.error('Error loading product:', error);
          loadingEl.innerHTML = '<p>Error loading product. Please try again.</p>';
        }
      }

      renderProduct() {
        if (!this.productData) return;

        const product = this.productData;
        this.selectedVariant = product.variants[0];
        this.hasVariants = false; // Initialize
        
        // Set title
        const titleEl = this.querySelector('.quick-view-modal__title');
        if (titleEl) {
          titleEl.textContent = product.title;
        }

        // Set price
        this.updatePrice();

        // Set description - render HTML tags (bold, italic, etc.)
        const descEl = this.querySelector('.quick-view-modal__description');
        if (descEl) {
          const description = product.description || '';
          // Render HTML content (preserves bold, italic, links, etc.)
          descEl.innerHTML = description;
        }

        // Render variant options (this will set this.hasVariants)
        this.renderVariants();

        // Update add to cart button with initial variant ID
        this.updateAddToCartButton();

        // Setup add to cart event handler (uses this.hasVariants to determine behavior)
        this.setupAddToCart();

        // Render images
        this.images = product.images || [];
        this.renderCarousel();
      }

      updatePrice() {
        const priceEl = this.querySelector('.quick-view-modal__price');
        if (!priceEl || !this.selectedVariant) return;

        const price = (this.selectedVariant.price / 100).toFixed(2);
        const comparePrice = this.selectedVariant.compare_at_price ? (this.selectedVariant.compare_at_price / 100).toFixed(2) : null;
        
        if (comparePrice && comparePrice > price) {
          priceEl.innerHTML = `
            <span class="quick-view-price--compare">$${comparePrice}</span>
            <span class="quick-view-price--current">$${price}</span>
          `;
        } else {
          priceEl.innerHTML = `<span class="quick-view-price--current">$${price}</span>`;
        }
      }

      renderVariants() {
        const variantsContainer = this.querySelector('.quick-view-modal__variants');
        if (!variantsContainer || !this.productData) return;

        variantsContainer.innerHTML = '';
        const product = this.productData;

        // If only one variant with title "Default Title", skip rendering
        if (product.variants.length === 1 && product.variants[0].title === 'Default Title') {
          this.hasVariants = false;
          return;
        }

        // Track if we render a color variant select dropdown
        let hasColorSelect = false;

        product.options.forEach((option, optionIndex) => {
          if (option.name === 'Title') return; // Skip default option

          // Check if this is a color variant option
          const isColorOption = option.name.toLowerCase().includes('color') || 
                                option.name.toLowerCase().includes('colour');

          const optionDiv = document.createElement('div');
          optionDiv.className = 'quick-view-option';
          
          const label = document.createElement('label');
          label.textContent = option.name;
          label.className = 'quick-view-option__label';
          optionDiv.appendChild(label);

          const select = document.createElement('select');
          select.className = 'quick-view-option__select';
          select.setAttribute('data-option-index', optionIndex);
          
          option.values.forEach((value) => {
            const optionEl = document.createElement('option');
            optionEl.value = value;
            optionEl.textContent = value;
            select.appendChild(optionEl);
          });

          select.addEventListener('change', () => this.handleVariantChange());
          optionDiv.appendChild(select);
          variantsContainer.appendChild(optionDiv);
          
          // Track if we rendered a color select
          if (isColorOption) {
            hasColorSelect = true;
          }
        });

        // hasVariants is true only when we rendered a color variant select dropdown
        this.hasVariants = hasColorSelect;
      }

      handleVariantChange() {
        const selects = this.querySelectorAll('.quick-view-option__select');
        const selectedOptions = Array.from(selects).map(select => select.value);
        
        // Find matching variant
        const variant = this.productData.variants.find(v => {
          const variantOptions = [v.option1, v.option2, v.option3].filter(Boolean);
          return selectedOptions.every((option, index) => option === variantOptions[index]);
        });

        if (variant) {
          this.selectedVariant = variant;
          this.updatePrice();
          this.updateAddToCartButton();
        }
      }

      updateAddToCartButton() {
        const addToCartBtn = this.querySelector('.quick-view-modal__add-to-cart');
        if (!addToCartBtn || !this.selectedVariant) return;

        // Update data-variant-id attribute for add-to-cart-10x functionality
        addToCartBtn.setAttribute('data-variant-id', this.selectedVariant.id);
      }

      setupAddToCart() {
        const addToCartBtn = this.querySelector('.quick-view-modal__add-to-cart');
        if (!addToCartBtn) return;

        // Ensure data-variant-id and data-quantity are set
        if (this.selectedVariant) {
          addToCartBtn.setAttribute('data-variant-id', this.selectedVariant.id);
          addToCartBtn.setAttribute('data-quantity', '1');
        }

        // If product has variant dropdown (hasVariants = true), use Buy Now → checkout
        if (this.hasVariants) {
          // Remove add-to-cart-10x class to prevent conflicts
          addToCartBtn.classList.remove('add-to-cart-10x');
          addToCartBtn.textContent = 'BUY NOW';
          
          // Intercept click and redirect to checkout
          addToCartBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (!this.selectedVariant) return;

            // Disable button during processing
            addToCartBtn.disabled = true;
            const originalText = addToCartBtn.textContent;
            addToCartBtn.textContent = 'PROCESSING...';

            try {
              // Add to cart via AJAX
              const formData = {
                items: [{
                  id: this.selectedVariant.id,
                  quantity: 1
                }]
              };

              const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
              });

              if (response.ok) {
                // Close modal
                this.hide();
                
                // Redirect to checkout
                window.location.href = '/checkout';
              } else {
                throw new Error('Failed to add to cart');
              }
            } catch (error) {
              console.error('Error adding to cart:', error);
              addToCartBtn.textContent = 'ERROR - TRY AGAIN';
              setTimeout(() => {
                addToCartBtn.textContent = originalText;
                addToCartBtn.disabled = false;
              }, 2000);
            }
          });
        } else {
          // No variants - use add-to-cart-10x class and data-variant-id (same as Mini and Discovery)
          addToCartBtn.textContent = 'ADD TO CART';
          addToCartBtn.classList.add('add-to-cart-10x');
          
          // Ensure data-variant-id is set for add-to-cart-10x functionality
          if (this.selectedVariant) {
            addToCartBtn.setAttribute('data-variant-id', this.selectedVariant.id);
            addToCartBtn.setAttribute('data-quantity', '1');
          }
          
          // Add click handler to close modal after add-to-cart event
          // The add-to-cart-10x handler will handle the actual cart addition
          addToCartBtn.addEventListener('click', (e) => {
            // Don't prevent default - let add-to-cart-10x handle it
            // Small delay to allow the add-to-cart-10x handler to execute first
            setTimeout(() => {
              this.hide();
            }, 300);
          });
        }
      }

      renderCarousel() {
        const track = this.querySelector('.quick-view-carousel__track');
        const thumbnailsContainer = this.querySelector('.quick-view-carousel__thumbnails');
        
        if (!track || !thumbnailsContainer) return;

        track.innerHTML = '';
        thumbnailsContainer.innerHTML = '';

        if (this.images.length === 0) {
          track.innerHTML = '<div class="quick-view-carousel__slide"><p>No images available</p></div>';
          return;
        }

        this.images.forEach((image, index) => {
          const separator = image.includes('?') ? '&' : '?';
          
          // Create main slide
          const slide = document.createElement('div');
          slide.className = 'quick-view-carousel__slide';
          slide.setAttribute('data-index', index);
          
          const img = document.createElement('img');
          // Use appropriate width for main carousel image
          img.src = `${image}${separator}width=800`;
          img.srcset = `${image}${separator}width=400 400w, ${image}${separator}width=600 600w, ${image}${separator}width=800 800w`;
          img.sizes = '(max-width: 749px) 100vw, (max-width: 990px) 50vw, 400px';
          img.alt = this.productData?.title || 'Product image';
          img.loading = index === 0 ? 'eager' : 'lazy';
          slide.appendChild(img);
          track.appendChild(slide);

          // Create thumbnail
          const thumbnail = document.createElement('button');
          thumbnail.className = 'quick-view-carousel__thumbnail';
          thumbnail.setAttribute('aria-label', `View image ${index + 1}`);
          thumbnail.setAttribute('data-index', index);
          thumbnail.setAttribute('type', 'button');
          
          const thumbnailImg = document.createElement('img');
          // Use smaller width for thumbnails (150px)
          thumbnailImg.src = `${image}${separator}width=150`;
          thumbnailImg.alt = `${this.productData?.title || 'Product'} thumbnail ${index + 1}`;
          thumbnailImg.loading = 'lazy';
          thumbnail.appendChild(thumbnailImg);
          
          thumbnail.addEventListener('click', () => this.goToImage(index));
          thumbnailsContainer.appendChild(thumbnail);
        });

        this.currentIndex = 0;
        this.updateCarousel();
      }

      updateCarousel() {
        const slides = this.querySelectorAll('.quick-view-carousel__slide');
        const thumbnails = this.querySelectorAll('.quick-view-carousel__thumbnail');
        const prevButton = this.querySelector('.quick-view-carousel__prev');
        const nextButton = this.querySelector('.quick-view-carousel__next');
        const thumbnailsContainer = this.querySelector('.quick-view-carousel__thumbnails');

        slides.forEach((slide, index) => {
          slide.classList.toggle('active', index === this.currentIndex);
        });

        thumbnails.forEach((thumbnail, index) => {
          thumbnail.classList.toggle('active', index === this.currentIndex);
        });

        // Update button states
        if (prevButton) {
          prevButton.disabled = this.currentIndex === 0;
        }
        if (nextButton) {
          nextButton.disabled = this.currentIndex === this.images.length - 1;
        }

        // Scroll thumbnail into view to follow the current image
        if (thumbnailsContainer && thumbnails[this.currentIndex]) {
          const activeThumbnail = thumbnails[this.currentIndex];
          const containerWidth = thumbnailsContainer.offsetWidth;
          const thumbnailWidth = activeThumbnail.offsetWidth;
          const thumbnailOffset = activeThumbnail.offsetLeft;
          const scrollPosition = thumbnailOffset - (containerWidth / 2) + (thumbnailWidth / 2);
          
          thumbnailsContainer.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          });
        }
      }

      nextImage() {
        if (this.currentIndex < this.images.length - 1) {
          this.currentIndex++;
          this.updateCarousel();
        }
      }

      prevImage() {
        if (this.currentIndex > 0) {
          this.currentIndex--;
          this.updateCarousel();
        }
      }

      goToImage(index) {
        if (index >= 0 && index < this.images.length) {
          this.currentIndex = index;
          this.updateCarousel();
          
          // Smooth scroll thumbnail into view with better positioning
          const thumbnailsContainer = this.querySelector('.quick-view-carousel__thumbnails');
          const activeThumbnail = this.querySelector(`.quick-view-carousel__thumbnail[data-index="${index}"]`);
          
          if (thumbnailsContainer && activeThumbnail) {
            // Calculate scroll position to center the thumbnail
            const containerWidth = thumbnailsContainer.offsetWidth;
            const thumbnailWidth = activeThumbnail.offsetWidth;
            const thumbnailOffset = activeThumbnail.offsetLeft;
            const scrollPosition = thumbnailOffset - (containerWidth / 2) + (thumbnailWidth / 2);
            
            thumbnailsContainer.scrollTo({
              left: scrollPosition,
              behavior: 'smooth'
            });
          }
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
        this.removeTrapFocus();
      }

      trapFocus() {
        const focusableElements = this.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          this.firstFocusableElement = focusableElements[0];
          this.lastFocusableElement = focusableElements[focusableElements.length - 1];
          
          this.addEventListener('keydown', this.handleTabKey);
          this.firstFocusableElement.focus();
        }
      }

      removeTrapFocus() {
        this.removeEventListener('keydown', this.handleTabKey);
      }

      handleTabKey = (e) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === this.firstFocusableElement) {
            e.preventDefault();
            this.lastFocusableElement.focus();
          }
        } else {
          if (document.activeElement === this.lastFocusableElement) {
            e.preventDefault();
            this.firstFocusableElement.focus();
          }
        }
      };
    }
  );
}

// Initialize quick view buttons
document.addEventListener('DOMContentLoaded', () => {
  const quickViewButtons = document.querySelectorAll('[data-quick-view]');
  const modal = document.querySelector('quick-view-modal');

  if (!modal) return;

  quickViewButtons.forEach((button) => {
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const productHandle = button.getAttribute('data-quick-view');
      if (productHandle) {
        modal.show(productHandle);
      }
    });
  });
});
